import Bugsnag from '@bugsnag/js';
import type { AxiosInstance } from 'axios';
import { createHttp, http } from '@/http/axios';
import {
  isLiveApiFallbackError,
  parseCanaryPercent,
  readCanaryBucket,
  shouldUseLiveApi,
} from '@/lib/liveApiRouting';

const LIVE_API_URL = import.meta.env.VITE_LIVE_API_URL as string | undefined;
const LIVE_API_CANARY_PERCENT = parseCanaryPercent(
  import.meta.env.VITE_LIVE_API_CANARY_PERCENT as string | undefined,
);
/** Court par rapport aux 20 s de welya-api : un live-api muet doit basculer vite sur le repli. */
const LIVE_API_TIMEOUT_MS = 5000;
const FALLBACK_REPORT_MESSAGE = 'Live API fallback';

const bugsnagApiKey = import.meta.env.VITE_BUGSNAG_API_KEY;
const isBugsnagEnabled = bugsnagApiKey !== undefined && bugsnagApiKey !== '';

let liveApiHttp: AxiosInstance | null | undefined;
let fallbackReported = false;

function browserStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function resolveLiveApiHttp(): AxiosInstance | null {
  if (liveApiHttp === undefined) {
    const enabled = shouldUseLiveApi(LIVE_API_URL, LIVE_API_CANARY_PERCENT, () =>
      readCanaryBucket(browserStorage()),
    );
    liveApiHttp = enabled && LIVE_API_URL ? createHttp(LIVE_API_URL, LIVE_API_TIMEOUT_MS) : null;
  }
  return liveApiHttp;
}

/** Un seul rapport par chargement de page : un incident live-api ne doit pas inonder Bugsnag. */
function reportFallback(path: string, error: unknown): void {
  if (!isBugsnagEnabled || fallbackReported) return;
  fallbackReported = true;
  const { status, code } = (error ?? {}) as { status?: number; code?: string };
  try {
    Bugsnag.notify(new Error(FALLBACK_REPORT_MESSAGE), (event) => {
      event.severity = 'warning';
      event.addMetadata('liveApiFallback', { path, status: status ?? null, code: code ?? null });
    });
  } catch {
    // le repli reste effectif même si le rapport échoue
  }
}

/** Lectures publiques servies par welya-live-api pendant le canary, avec repli sur welya-api. */
export async function getPublicRead<T>(path: string): Promise<T> {
  const liveApi = resolveLiveApiHttp();
  if (liveApi) {
    try {
      return (await liveApi.get<T>(path)).data;
    } catch (error) {
      if (!isLiveApiFallbackError(error)) throw error;
      reportFallback(path, error);
    }
  }
  return (await http.get<T>(path)).data;
}
